import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { calendarAPI } from "../../services/api";
import { CalendarResponse, CalendarState } from "../../types/calendar";

function getPeriodDates(mode: "week" | "day", referenceDate?: string) {
  const date = referenceDate ? new Date(referenceDate) : new Date();

  let start = new Date(date);
  let end = new Date(date);

  if (mode === "week") {
    const day = date.getDay(); // 0 = Sunday
    const diffToMonday = day === 0 ? -6 : 1 - day; // Move to Monday
    start.setDate(date.getDate() + diffToMonday);
    start.setHours(0, 0, 0, 0);

    end = new Date(start);
    end.setDate(start.getDate() + 6);
    end.setHours(23, 59, 59, 999);
  } else {
    start.setHours(0, 0, 0, 0);
    end.setHours(23, 59, 59, 999);
  }

  return {
    start: start.toISOString(),
    end: end.toISOString(),
  };
}

const { start, end } = getPeriodDates("week");

// Async thunk — now sends start/end to backend
export const fetchCalendar = createAsyncThunk<
  CalendarResponse,
  { mode?: "week" | "day"; date?: string } | undefined
>("calendar/fetchCalendar", async (params, { rejectWithValue }) => {
  try {
    const mode = params?.mode || "week";
    const date = params?.date;
    const { start, end } = getPeriodDates(mode, date);

    const response = await calendarAPI.getCalendarData({ start, end });
    return response.data;
  } catch (err: any) {
    return rejectWithValue(
      err.response?.data?.message || "Failed to load calendar",
    );
  }
});

const initialState: CalendarState = {
  vehicles: [],
  shipments: [],
  meta: { countVehicles: 0, countShipments: 0, tz: undefined },
  loading: false,
  error: null,
  viewMode: "day",
  startISO: start,
  endISO: end,
};

const calendarSlice = createSlice({
  name: "calendar",
  initialState,
  reducers: {
    setViewMode(state, action: PayloadAction<"week" | "day">) {
      state.viewMode = action.payload;
      const { start, end } = getPeriodDates(action.payload);
      state.startISO = start;
      state.endISO = end;
    },
    setReferenceDate(state, action: PayloadAction<string>) {
      const { start, end } = getPeriodDates(state.viewMode, action.payload);
      state.startISO = start;
      state.endISO = end;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchCalendar.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCalendar.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        state.vehicles = action.payload.vehicles || [];
        state.shipments = action.payload.shipments || [];

        state.meta = {
          countVehicles:
            action.payload.meta?.countVehicles ?? state.vehicles.length,
          countShipments:
            action.payload.meta?.countShipments ?? state.shipments.length,
          tz: action.payload.meta?.tz,
        };
      })
      .addCase(fetchCalendar.rejected, (state, action) => {
        state.loading = false;
        state.error = (action.payload as string) || "Failed to fetch calendar";
      });
  },
});

export const { setViewMode, setReferenceDate } = calendarSlice.actions;
export default calendarSlice.reducer;
